import * as React from 'react';
import * as ShowOrganization from '../../../core/gql/queries/show-organizations';
import * as QueryTags from '../../../core/gql/queries/tags';
import * as CreateProgram from '../../../core/gql/mutations/create-program-extended';
import './program-guide.scss';
import ProgramTypes from './program-types';
import SportsCards from './sports-cards';
import ConfirmationModal, { ConfirmationType } from './confirmation-modal';
import ProgramGuidBreadcrumb, { BreadcrumbSection } from '../../common/breadcrumb';
import { Stepper } from 'frontend-toolkit';
import { useMutation, useQuery } from '@apollo/client';
import { Avatar, Field, Icon } from '@teamsnap/teamsnap-ui';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import SeasonGuide from '../season-guide/season-guide';
import { GqlProgramExpanded } from '../../../core/gql/fragments/program';
import { TagType } from '../../../models/tag';

export type Props = {
  orgId: number;
  isOpen: boolean;
  setOpen: (x: boolean) => void;
  whenProgramCreated?: () => void;
};

export enum ProgramGuideSteps {
  TYPE = 'Program Type',
  SPORT = 'Sport',
  NAME = 'Name and Description',
}

export interface ProgramData {
  type: string;
  sport: number;
  name: string;
  description: string;
}

const PROGRAM_DEFAULTS = { type: '', sport: null, name: '', description: '' };

export const CustomHeader = ({ closeStepper, text }: { closeStepper: () => void; text: string }) => (
  <header className="guide-header u-flex u-flexJustifyBetween u-flexAlignItemsCenter u-padLg u-borderBottom u-borderNeutral4">
    <h3 className="u-flex u-flexAlignItemsCenter">
      <Avatar
        src="https://storage.googleapis.com/ts_assets_prod-classic_assets/logo-icon-dafd29abff7b6ca55ad71c35bd34d679.png"
        size="sm"
        mods="u-spaceRightMd guided-icon"
      />
      {text}
    </h3>
    <button type="button" className="guide-close-btn" onClick={closeStepper}>
      <Icon name="dismiss" mods="u-fontSizeLg" />
    </button>
  </header>
);

const ProgramGuide = ({ orgId, isOpen, setOpen, whenProgramCreated }: Props) => {
  const [program, setProgram] = React.useState<ProgramData>(PROGRAM_DEFAULTS);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [newProgram, setNewProgram] = React.useState<GqlProgramExpanded>(null);

  const breadcrumbDefaults: BreadcrumbSection[] = [
    {
      name: 'Basic Program Info',
      requiredData: ['type', 'sport', 'name'],
      onClick: () => {
        updateCurrentBreadcrumb(ProgramGuideSteps.TYPE);
        setCurrentStep(0);
      },
      children: [
        {
          name: ProgramGuideSteps.TYPE,
          requiredData: ['type'],
          onClick: () => {
            updateCurrentBreadcrumb(ProgramGuideSteps.TYPE);
            setCurrentStep(0);
          },
        },
        {
          name: ProgramGuideSteps.SPORT,
          requiredData: ['sport'],
          onClick: () => {
            updateCurrentBreadcrumb(ProgramGuideSteps.SPORT);
            setCurrentStep(1);
          },
        },
        {
          name: ProgramGuideSteps.NAME,
          requiredData: ['name'],
          onClick: () => {
            updateCurrentBreadcrumb(ProgramGuideSteps.NAME);
            setCurrentStep(2);
          },
        },
      ],
    },
  ];

  const [breadcrumb, updateBreadcrumb] = React.useState(breadcrumbDefaults);
  const [showSeasonStepper, setShowSeasonStepper] = React.useState(false);
  const [currentBreadcrumb, updateCurrentBreadcrumb] = React.useState(ProgramGuideSteps.TYPE);

  const getOrganization = useQuery(ShowOrganization.QUERY, {
    variables: { organizationId: orgId },
    skip: !orgId,
  });

  const getOrganizationTags = useQuery(QueryTags.QUERY, { variables: { organizationId: orgId } });

  const [createProgram] = useMutation(CreateProgram.MUTATION);

  const closeStepper = () => {
    setOpen(false);
    resetProgramGuide();
  };

  const resetProgramGuide = () => {
    setCurrentStep(0);
    updateBreadcrumb(breadcrumbDefaults);
    setProgram(PROGRAM_DEFAULTS);
    updateCurrentBreadcrumb(ProgramGuideSteps.TYPE);
  };

  const submitCreateProgram = async (continueToSeason: boolean) => {

    try {
      const resp = await createProgram({
        variables: {
          isCoed: false,
          description: program.description,
          name: program.name,
          organizationId: orgId,
          sportId: program.sport,
          type: program.type,
          areaOfOperation: getOrganizationTags.data?.tags?.find((tag) => tag.type === TagType.AREA_OF_OPERATION)?.id,
        },
      });

      reactiveFeedbackMessage({
        message: `The program ${resp.data.createProgram.name} has been created.`,
        isError: false,
        autoclose: true,
      });
      whenProgramCreated && whenProgramCreated();

      setNewProgram(resp.data.createProgram);


      if (continueToSeason) {
        setShowSeasonStepper(true);
      }
    } catch (e) {
      const errorMessage = 'Program create failed. Please try again.';
      reactiveFeedbackMessage({
        message: errorMessage,
        isError: true,
        autoclose: true,
      });
      console.warn('Failed to properly validate and submit program and season forms.');
    }

    setShowConfirmModal(false);
    closeStepper();
  };

  return (
    <>
      <ConfirmationModal
        title={program.name.trim()}
        show={showConfirmModal}
        onSubmit={submitCreateProgram}
        confirmationType={ConfirmationType.PROGRAM}
        onClose={() => {
          closeStepper();
          setShowConfirmModal(false);
        }}
      />

      {showSeasonStepper && (
        <SeasonGuide
          setOpen={setShowSeasonStepper}
          organization={getOrganization.data?.showOrganization}
          program={newProgram}
        />
      )}

      {isOpen && (
        <Stepper
          show
          showProgress={false}
          activeStep={currentStep}
          onClose={closeStepper}
          displayBackOnFirst={false}
          drawer={<ProgramGuidBreadcrumb current={currentBreadcrumb} breadcrumb={breadcrumb} data={program} />}
          customHeader={<CustomHeader text={getOrganization.data?.showOrganization.name} closeStepper={closeStepper} />}
          customStepStyles={{ paddingLeft: '300px' }}
          steps={[
            {
              title: 'Type',
              value: program.type,
              heading: 'What type of program is this?',
              disableNext: program.type === '',
              elements: ProgramTypes.map((type) => ({
                type: 'radio',
                name: 'type',
                text: type.label,
                selected: program.type === type.id,
                callback: (_, { type }) => {
                  setProgram({
                    ...program,
                    type: ProgramTypes.find((x) => x.label === type).id,
                  });
                },
              })),
              stepCallback: () => {
                updateCurrentBreadcrumb(ProgramGuideSteps.SPORT);
                setCurrentStep(1);
              },
            },
            {
              title: 'Sport',
              disableNext: program.sport === null,
              heading: 'What sport does this program play?',
              elements: (
                <SportsCards
                  selectedSport={program.sport}
                  setSelectedSport={(sport) => setProgram({ ...program, sport })}
                />
              ),
              stepBackCallback: () => {
                updateCurrentBreadcrumb(ProgramGuideSteps.TYPE);
                setCurrentStep(0);
              },
              stepCallback: () => {
                updateCurrentBreadcrumb(ProgramGuideSteps.NAME);
                setCurrentStep(2);
              },
            },
            {
              title: 'Name and description',
              disableNext: program.type === '' || program.sport === null || program.name.trim() === '',
              heading: 'Name this program *',
              stepBackCallback: () => {
                updateCurrentBreadcrumb(ProgramGuideSteps.SPORT);
                setCurrentStep(1);
              },
              stepCallback: () => {
                setOpen(false);
                setShowConfirmModal(true);
              },
              elements: (
                <section style={{ width: '608px' }}>
                  <Field
                    data-testid="program-guide-name"
                    type="text"
                    name="program name"
                    caption={`Editing program name won't change your program type or sport.
                To change your program type or sport, please go back to the previous steps.`}
                    formFieldProps={{
                      size: 'large',
                      placeholder: 'e.g. Competitive Soccer',
                      inputProps: {
                        value: program.name,
                        onChange: (e) => setProgram({ ...program, name: e.target.value }),
                      },
                    }}
                  />

                  <h2 style={{ margin: '36px 0 21px 0' }}>How would you describe your program?</h2>
                  {/* TO DO: Add textarea option to TeamSnap UI */}
                  <textarea
                    data-testid="program-guide-description"
                    placeholder="Description-how would you like to describe this program on your website?"
                    className="program-description"
                    value={program.description}
                    maxLength={255}
                    onChange={(e) => setProgram({ ...program, description: e.target.value })}
                  />
                  <p className="program-description-caption">255 max character limit</p>
                </section>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

export default ProgramGuide;
